import React, { useState, useEffect, useRef } from 'react';
import './HeroSection.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BASE_URL } from '../constants';

const API_SEARCH = `${BASE_URL}api/streets/search-streets?q=`; 

const HeroSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [error, setError] = useState('');

  const searchRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery || !searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        const ac = new AbortController();
        abortRef.current = ac;

        const token = localStorage.getItem('token');
        const headers = { Accept: 'application/json' };
        if (token) headers['Authorization'] = `Token ${token}`;

        const res = await fetch(API_SEARCH + encodeURIComponent(searchQuery.trim()), {
          signal: ac.signal,
          headers,
        });

        if (!res.ok) {
          setSuggestions([]);
          setShowSuggestions(false);
        } else {
          const data = await res.json();
          setSuggestions(Array.isArray(data) ? data : []);
          setShowSuggestions(true);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Search error', err);
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(t);
    };
  }, [searchQuery]);

  const handleSuggestionClick = (street) => {
    setSelectedStreet(street);
    setSearchQuery(`${street.name}, ${street.postcode}`);
    setShowSuggestions(false);
    setSuggestions([]);

    const queryString = `?street=${encodeURIComponent(street.name)}&street_id=${encodeURIComponent(street.id)}`;

    if (user) {
      navigate(`/analysis${queryString}`);
    } else {
      navigate('/login', { state: { fromSearch: true, street: street.name, street_id: street.id } });
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!searchQuery.trim()) {
      setError('Please enter a street name or postcode');
      return;
    }

    if (selectedStreet) {
      const s = selectedStreet;
      const qs = `?street=${encodeURIComponent(s.name)}&street_id=${encodeURIComponent(s.id)}`;
      if (user) navigate(`/analysis${qs}`);
      else navigate('/login', { state: { fromSearch: true, street: s.name, street_id: s.id } });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Accept: 'application/json' };
      if (token) headers['Authorization'] = `Token ${token}`;

      const res = await fetch(API_SEARCH + encodeURIComponent(searchQuery.trim()), { headers });
      if (!res.ok) {
        setError('Search failed');
        return;
      }
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        const qs = `?street=${encodeURIComponent(first.name)}&street_id=${encodeURIComponent(first.id)}`;
        if (user) navigate(`/analysis${qs}`);
        else navigate('/login', { state: { fromSearch: true, street: first.name, street_id: first.id } });
      } else {
        setError('No street found for that query');
      }
    } catch (err) {
      console.error(err);
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <header className="hero-section" style={{ paddingTop: '7rem' }}>
      <div className="hero-content">
        <h1 className="hero-title">
          Amenity<span className="hero-title-accent">Scores</span>
        </h1>
        <p className="hero-subtitle">Sell the lifestyle, not just the property.</p>

        <div className="search-container" ref={searchRef}>
          <form onSubmit={handleSearchSubmit} className="search-form" aria-label="Search streets">
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSelectedStreet(null); setError(''); }}
                placeholder="Enter Street Name or Postcode"
                className="search-input"
                autoComplete="off"
              />
              {loading && <div className="loading-spinner" />}
              {showSuggestions && suggestions.length > 0 && (
                <div className="suggestions-dropdown" role="listbox">
                  {suggestions.map((s) => (
                    <div key={s.id} className="suggestion-item" onMouseDown={() => handleSuggestionClick(s)}>
                      <div className="suggestion-name">{s.name}</div>
                      <div className="suggestion-postcode">{s.postcode}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="search-button">Analyse Street</button>
          </form>

          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="popular-searches"><p>Popular searches: Skelbrook, Glenburnie, Amity</p></div>
      </div>
    </header>
  );
};

export default HeroSection;
